import React, {useEffect, useMemo, useRef, useState} from 'react';

import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StopRounded from '@mui/icons-material/StopRounded'; 
import TipsAndUpdatesOutlined from '@mui/icons-material/TipsAndUpdatesOutlined';
import TuneRounded from '@mui/icons-material/TuneRounded';

import SearchRangeModal from './SearchRangeModal';

import styles from './styles/input-box.module.css';

export default function InputBox({ isInitialtScreen, isInitialChat, message, placeholder, isSendMessage, needFeedback, botList, currentBotId, handleChangeMessage, handleSendMessage, handleStopMessage, handleChangeCurrentBot }) {
    const textareaRef = useRef(null);

    const [isActiveThinkingMode, setIsActiveThinkingMode] = useState(false);
    const [tuneButtonElement, setTuneButtonElement] = useState(null); // Popoover(모달)의 기준이 되는 검색 범위 설정 버튼 요소

    const isOpenSearchRangeModal = Boolean(tuneButtonElement);
    const isOnboardingScreen = isInitialtScreen && isInitialChat;
    const textAreaHeight = isOnboardingScreen ? '54px' : '27px';

    const getCurrentBot = useMemo(() => { // currentBotId로 현재 봇 객체 찾기
        return (botList ?? []).find(bot => bot.id === currentBotId) ?? null;
    }, [botList, currentBotId]);

    const getCurrentBotInfo = useMemo(() => { // 봇 타입에 따른 아이콘과 텍스트 가져오기
        if (!getCurrentBot) return null;

        const currentBotType = (getCurrentBot.type || '').toUpperCase();
        
        return BOT_TYPE_MAP[currentBotType] ?? null;
    }, [getCurrentBot]);

    const currentBotIcon = getCurrentBotInfo?.Icon ?? null;
    const currentBotText = getCurrentBotInfo?.text ?? null;

    const { icon: Icon, backgroundColor, onClick } = getSendButtonState({ message, isSendMessage, needFeedback, handleSendMessage, handleStopMessage });

    const toggleThinkingMode = () => {
        setIsActiveThinkingMode(!isActiveThinkingMode);
    };

    const toggleSearchRangeModal = (e) => {
        setTuneButtonElement(prev => (prev ? null : e.currentTarget));
    };

    const handleSendMessageWithEnter = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(message);
        }
    };

    const handleInput = () => {
        const textarea = textareaRef.current;

        if (!textarea) return;

        textarea.style.height = textAreaHeight; // 높이 초기화
        textarea.style.height = `${textarea.scrollHeight}px`; // 높이 확장

        // 최대 높이 제한
        const maxHeight = 135; // 27 x 5 (한 줄 높이 x 최대 줄 수)
        if (textarea.scrollHeight > maxHeight) {
            textarea.style.height = `${maxHeight}px`;
            textarea.style.overflowY = 'auto';
        } else {
            textarea.style.overflowY = 'hidden';
        }
    };

    // 컴포넌트 마운트 시 초기 높이 설정
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = textAreaHeight;
        }
    }, [isInitialtScreen, isInitialChat]);

    return (
        <Box sx={{ width: isOnboardingScreen ? 1000 : 'calc(100% - 80px)', padding: '24px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '24px', border: '1px solid', borderColor: 'grey.400', borderRadius: '8px' }}>
            <textarea
                ref={textareaRef}
                className={styles.textarea}
                placeholder={placeholder}
                name='message'
                value={message}
                onChange={handleChangeMessage}
                onKeyDown={handleSendMessageWithEnter}
                onInput={handleInput}
                autoComplete='off'
            />
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
                    <Box sx={{ display: 'inline-block', position: 'relative' }}>
                        <IconButton
                            data-tune-button 
                            sx={{ width: '40px', height: '40px', border: '1px solid', backgroundColor: isOpenSearchRangeModal ? 'grey.100' : 'transparent', borderColor: 'grey.400', borderRadius: '8px' }}
                            size='medium'
                            onClick={toggleSearchRangeModal}
                        >
                            <TuneRounded />
                        </IconButton>
                        <SearchRangeModal
                            tuneButtonElement={tuneButtonElement}
                            isOpenSearchRangeModal={isOpenSearchRangeModal}
                            isOnboardingScreen={isOnboardingScreen}
                            botList={botList}
                            currentBotId={currentBotId}
                            closeSearchRangeModal={toggleSearchRangeModal}
                            handleChangeCurrentBot={handleChangeCurrentBot}
                        />
                    </Box>
                    {getCurrentBot && currentBotIcon && currentBotText && (currentBotText !== 'DB 검색') &&
                        <Box
                            sx={{
                                height: '40px',
                                padding: '8px 22px',
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                backgroundColor: 'primary.selected',
                                border: '1px dashed',
                                borderColor: 'primary.outlinedBorder',
                                borderRadius: '4px',
                                color: 'primary.main'
                            }}
                        >
                            {React.createElement(currentBotIcon)}
                            <Typography variant='body3'>{currentBotText}</Typography>
                        </Box>
                    }
                    {/* <Tooltip
                        title='심층 사고 모드'
                        placement='bottom'
                        arrow
                        disableFocusListener // 키보드 포커스 끄기
                        disableTouchListener // 터치 끄기
                    >
                        <IconButton
                            sx={{ width: '40px', height: '40px', backgroundColor: isActiveThinkingMode ? 'action.hover' : 'transparent', border: '1px solid', borderColor: isActiveThinkingMode ? 'primary.main' : 'grey.400', borderRadius: '8px' }}
                            size='medium'
                            onClick={toggleThinkingMode}
                        >
                            <TipsAndUpdatesOutlined sx={{ color: isActiveThinkingMode ? 'primary.main' : 'action.active' }} />
                        </IconButton>
                    </Tooltip> */}
                </Box>
                <IconButton
                    sx={{ width: '40px', height: '40px', backgroundColor, borderRadius: '4px', color: '#FFF', '&:hover': { backgroundColor } }}
                    size='medium'
                    onClick={onClick}
                >
                    <Icon />    
                </IconButton>
            </Box>
        </Box>
    );
}

const BOT_TYPE_MAP = {
    DATABASE: { Icon: SearchRoundedIcon, text: 'DB 검색' },
    DOCUMENT: { Icon: LibraryBooksOutlinedIcon, text: '문서 검색' },
    EMAIL: { Icon: MailOutlineOutlinedIcon, text: 'GMail 검색' },
    FAQ: { Icon: HelpCenterOutlinedIcon, text: 'FAQ 검색' }
};

function getSendButtonState({ message, isSendMessage, needFeedback, handleSendMessage, handleStopMessage }) {
    if (isSendMessage) { // 답변 생성 중인 경우
        return {
            icon: StopRounded,
            backgroundColor: 'primary.main',
            onClick: handleStopMessage
        };
    }

    if (message.trim().length === 0 || needFeedback) { // 질문을 작성하지 않은 경우 or 이전 답변에 대한 피드백이 필요한 경우
        return {
            icon: ArrowUpward,
            backgroundColor: 'grey.500',
            onClick: null
        };
    }

    return {
        icon: ArrowUpward,
        backgroundColor: 'primary.main',
        onClick: () => handleSendMessage(message)
    };
}
